import { useState, useEffect } from 'react';
import { BookOpen, CheckCircle, Clock, Plus, Trash2, Save, CheckCircle2 } from 'lucide-react';
import { Card, SectionTitle, Button, TextArea, TextInput, Label } from '@/components/ui';

interface PlanModule {
  id: string;
  title: string;
}

interface TrainingWeekPlan {
  week: number;
  title: string;
  description: string;
  modules: PlanModule[];
}

const STORAGE_KEY = 'uisce_training_plan_custom_v1';

const defaultPlan: TrainingWeekPlan[] = [
  {
    week: 1,
    title: 'Week 1: Fundamentals & System Access',
    description: 'Introduction to Uisce Éireann commercial contract operations, system navigation, and basic customer service workflows.',
    modules: [
      { id: 'm1', title: 'Company Overview & Customer Operations Structure' },
      { id: 'm2', title: 'Billing Systems & CRM Navigation (Temetra / SAP)' },
      { id: 'm3', title: 'Security, Compliance & Data Protection (GDPR)' },
      { id: 'm4', title: 'Shadowing Live Calls & Call Structure Basics' }
    ]
  },
  {
    week: 2,
    title: 'Week 2: Advanced Queries & Practical Scenarios',
    description: 'Handling complex billing disputes, meter reads, leak allowances, and live mock call simulations.',
    modules: [
      { id: 'm5', title: 'Water Services Billing, Tariffs & Metering Rules' },
      { id: 'm6', title: 'Handling Dispute Resolution & Vulnerable Customers' },
      { id: 'm7', title: 'Advanced Mock Calls & Error Logging' },
      { id: 'm8', title: 'Irish-speaking Support Workflows & Terminology' }
    ]
  },
  {
    week: 3,
    title: 'Week 3: Call Accreditation & Live Floor Transition',
    description: 'Final call evaluations, accreditation assessments, and transition to live floor hypercare support.',
    modules: [
      { id: 'm9', title: 'Final Call Accreditation Assessments (Attempts 1-3)' },
      { id: 'm10', title: 'Mandatory Compliance Course Verification' },
      { id: 'm11', title: 'Hypercare Floor Readiness & Escalation Paths' },
      { id: 'm12', title: 'Graduation & Transition to Independent Operations' }
    ]
  }
];

export function TrainingPlanView() {
  const [plan, setPlan] = useState<TrainingWeekPlan[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse training plan', e);
      }
    }
    return defaultPlan;
  });

  const [newModuleText, setNewModuleText] = useState<{ [week: number]: string }>({});
  const [saved, setSaved] = useState(false);
  const userRole = localStorage.getItem('uisce-user-role');
  const isViewer = userRole === 'viewer';

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
  }, [plan]);

  const handleUpdateHeader = (weekNumber: number, field: 'title' | 'description', value: string) => {
    if (isViewer) return;
    setPlan((prev) =>
      prev.map((w) => (w.week === weekNumber ? { ...w, [field]: value } : w))
    );
  };

  const handleAddModule = (weekNumber: number) => {
    if (isViewer) return;
    const text = newModuleText[weekNumber]?.trim();
    if (!text) return;

    setPlan((prev) =>
      prev.map((w) => {
        if (w.week !== weekNumber) return w;
        return {
          ...w,
          modules: [...w.modules, { id: `mod-${Date.now()}`, title: text }]
        };
      })
    );

    setNewModuleText((prev) => ({ ...prev, [weekNumber]: '' }));
  };

  const handleDeleteModule = (weekNumber: number, moduleId: string) => {
    if (isViewer) return;
    setPlan((prev) =>
      prev.map((w) => {
        if (w.week !== weekNumber) return w;
        return {
          ...w,
          modules: w.modules.filter((m) => m.id !== moduleId)
        };
      })
    );
  };

  const handleSaveAll = () => {
    if (isViewer) {
      alert('Viewers have read-only access.');
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <SectionTitle
          title="Training Schedule & Plan"
          subtitle="Customize and manage the 3-week onboarding curriculum."
          icon={<BookOpen className="w-6 h-6" />}
        />
        {!isViewer && (
          <div className="flex items-center gap-3">
            {saved && (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 animate-fade-in">
                <CheckCircle2 className="h-4 w-4" /> Saved!
              </span>
            )}
            <Button onClick={handleSaveAll}>
              <Save className="w-4 h-4" /> Save Curriculum
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {plan.map((week) => (
          <Card key={week.week} className="p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="w-full space-y-3">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700 border border-blue-200">
                    <Clock className="w-3.5 h-3.5" /> Week {week.week}
                  </span>
                </div>
                <div>
                  <Label>Week Title</Label>
                  <TextInput
                    value={week.title}
                    onChange={(val) => handleUpdateHeader(week.week, 'title', val)}
                    className="font-bold text-slate-900"
                  />
                </div>
                <div>
                  <Label>Week Overview Description</Label>
                  <TextArea
                    value={week.description}
                    onChange={(val) => handleUpdateHeader(week.week, 'description', val)}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4 mt-4">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Key Modules & Milestones</h4>
              
              <div className="space-y-2 mb-4">
                {week.modules.map((mod) => (
                  <div key={mod.id} className="flex items-center justify-between gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <div className="flex items-center gap-2.5 flex-1">
                      <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="text-sm text-slate-700 font-medium">{mod.title}</span>
                    </div>
                    {!isViewer && (
                      <button
                        type="button"
                        onClick={() => handleDeleteModule(week.week, mod.id)}
                        className="text-slate-400 hover:text-red-600 p-1 rounded-lg transition-colors"
                        title="Remove Module"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {!isViewer && (
                <div className="flex items-center gap-2 mt-3">
                  <TextInput
                    value={newModuleText[week.week] || ''}
                    onChange={(val) => setNewModuleText({ ...newModuleText, [week.week]: val })}
                    placeholder="Add new module or milestone..."
                    className="flex-1"
                  />
                  <Button variant="secondary" onClick={() => handleAddModule(week.week)}>
                    <Plus className="w-4 h-4" /> Add Module
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
